import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute, useParams } from "@tanstack/react-router"
import type { ReservationMini } from "ipctg-inflow-types/InFlowReserveAPI"
import { motion } from "motion/react"
import { useMemo, useState } from "react"
import type { Key } from "react-aria-components"
import { useTranslation } from "react-i18next"

import Text from "@/globalComponents/atoms/Text"
import FilterTabs from "@/globalComponents/molecules/FilterTabs"
import { ReservationConfirmationCard } from "@/globalComponents/molecules/productCards/ReservationCard"
import { CancelConfirmedModal } from "@/globalComponents/molecules/productCards/ReservationCard/reservationModals/CancelConfirmedModal"
import { isCancelledStatus } from "@/utilities/etc"
import {
  cancelReservationMutationOptions,
  currentReservationsQueryOptions,
  pastReservationsQueryOptions,
  updateReservationPlateMutationOptions,
} from "@/utilities/hooks/queries/reserve"
import { useErrorModal } from "@/utilities/hooks/useErrorModal"
import { Crumb } from "@/utilities/types/local"

import styles from "./reservations.module.css"

const RESERVATION_TABS: Key[] = ["active", "past", "cancelled"]

const isCancelled = (reservation: ReservationMini) =>
  isCancelledStatus(reservation.status)

export const Route = createFileRoute("/_authenticated/account/reservations")({
  staticData: {
    pageName: "Reservations",
  },
  loader: async ({ context }) => {
    // No-QR fallback: null locationID returns all locations' reservations.
    await Promise.all([
      context.queryClient.ensureQueryData(
        currentReservationsQueryOptions(null)
      ),
      context.queryClient.ensureQueryData(pastReservationsQueryOptions(null)),
    ])
    return { crumb: { translationKey: "common:reservations.header" } as Crumb }
  },
  component: ReservationsRouteComponent,
})

export function ReservationsRouteComponent({
  locationID = null,
}: {
  locationID?: string | null
}) {
  const { t } = useTranslation("common")
  const { qrLookupID } = useParams({ strict: false })
  const [selectedTab, setSelectedTab] = useState<Key>("active")
  const [cancelConfirmedOpen, setCancelConfirmedOpen] = useState(false)
  const queryClient = useQueryClient()

  const { data: currentReservations } = useSuspenseQuery(
    currentReservationsQueryOptions(locationID)
  )
  const { data: pastReservations } = useSuspenseQuery(
    pastReservationsQueryOptions(locationID)
  )

  const { setError } = useErrorModal()
  const cancelReservationMutation = useMutation({
    ...cancelReservationMutationOptions(queryClient, locationID),
    onSuccess: () => setCancelConfirmedOpen(true),
    onError: (error) => setError({ error }),
  })
  const { mutate: handleUpdatePlate } = useMutation({
    ...updateReservationPlateMutationOptions(queryClient, locationID),
    onError: (error) => setError({ error }),
  })

  /**
   * Cancelled reservations get their own tab, so they are pulled out of both
   * API responses rather than being left in with the active and past ones.
   */
  const { active, past, cancelled } = useMemo(
    () => ({
      active: currentReservations.filter((r) => !isCancelled(r)),
      past: pastReservations.filter((r) => !isCancelled(r)),
      cancelled: [...currentReservations, ...pastReservations].filter(
        isCancelled
      ),
    }),
    [currentReservations, pastReservations]
  )

  const displayedReservations =
    selectedTab === "active"
      ? active
      : selectedTab === "past"
        ? past
        : cancelled

  const emptyStateCopy =
    selectedTab === "active"
      ? t("reservations.emptyActive")
      : selectedTab === "past"
        ? t("reservations.emptyPast")
        : t("reservations.emptyCancelled")

  return (
    <>
      <FilterTabs onSelectionChange={setSelectedTab} selectedKey={selectedTab}>
        <FilterTabs.Tab id="active">{t("reservations.active")}</FilterTabs.Tab>
        <FilterTabs.Tab id="past">{t("reservations.past")}</FilterTabs.Tab>
        <FilterTabs.Tab id="cancelled">
          {t("reservations.cancelled")}
        </FilterTabs.Tab>
      </FilterTabs>

      <FilterTabs.Panels tabOrder={RESERVATION_TABS} selectedKey={selectedTab}>
        {displayedReservations.length === 0 ? (
          <Text level={2} className={styles.emptyState}>
            {emptyStateCopy}
          </Text>
        ) : (
          <motion.div
            className={styles.cardList}
            initial="hidden"
            animate="visible"
            variants={{
              visible: { transition: { staggerChildren: 0.1 } },
            }}
          >
            {displayedReservations.map((reservation) => (
              <motion.div
                key={reservation.reservationID}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { ease: "easeIn" },
                  },
                }}
              >
                <ReservationConfirmationCard
                  reservation={reservation}
                  qrLookupID={qrLookupID}
                  contextLocationID={locationID}
                  {...(selectedTab === "active" && {
                    onSelectPlate: (plate: string) =>
                      handleUpdatePlate({
                        newPlate: plate,
                        reservationID: reservation.reservationID,
                        receiptID:
                          reservation.receiptIDs[
                            reservation.receiptIDs.length - 1
                          ],
                      }),
                    isCancelling:
                      cancelReservationMutation.isPending &&
                      cancelReservationMutation.variables?.reservationID ===
                        reservation.reservationID,
                    onCancelReservation: () =>
                      cancelReservationMutation.mutateAsync({
                        reservationID: reservation.reservationID,
                        enterpriseID: reservation.enterpriseID,
                        locationID: reservation.locationID,
                      }),
                  })}
                />
              </motion.div>
            ))}
          </motion.div>
        )}
      </FilterTabs.Panels>

      <CancelConfirmedModal
        isOpen={cancelConfirmedOpen}
        onOpenChange={() => setCancelConfirmedOpen(false)}
      />
    </>
  )
}
